import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, QrCode, Crown, ChevronRight, Wallet, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const PlanExpiredBlock = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user) fetchStatus();
    else setLoading(false);
    // Check if already dismissed this session
    if (sessionStorage.getItem('plan_expired_dismissed')) {
      setDismissed(true);
    }
  }, [user]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setStatus(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  // Only show if user had a paid plan that expired
  if (loading || !user || !status || !status.plan_expired_recently || dismissed) return null;

  // If currently on active paid plan or trial, don't block
  if (status.is_trial || status.subscription_type !== 'free') return null;

  const lastPlan = status.subscription_type === 'premium' ? 'premium_monthly' : 'basic_monthly';

  return (
    <motion.div
      className="fixed inset-0 z-[90] bg-[#050505] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="plan-expired-block"
    >
      <motion.div
        className="w-full max-w-md"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      >
        {/* Icon */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 mx-auto bg-amber-500/10 border border-amber-400/30 rounded-2xl flex items-center justify-center mb-4">
            <Lock className="w-10 h-10 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="expired-title">
            Seu plano venceu
          </h1>
          <p className="text-gray-400 text-sm max-w-xs mx-auto">
            Manter o controle custa menos que um juro de cheque especial.
            Copie o Pix abaixo e continue economizando.
          </p>
        </div>

        {/* Plan options */}
        <div className="space-y-3 mb-6">
          {/* Basic Plan */}
          <div 
            className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 hover:border-[#00FF94]/30 transition-all cursor-pointer"
            onClick={() => navigate('/pix?package=basic_monthly')}
            data-testid="expired-basic-plan"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#00FF94]" />
                <span className="font-bold text-white">IA Basico</span>
              </div>
              <span className="text-[#00FF94] font-bold text-sm">R$ 9,90/mes</span>
            </div>
            <p className="text-xs text-gray-400">5 creditos de IA por mes</p>
          </div>

          {/* Premium Plan */}
          <div 
            className="bg-[#0A0A0A] border border-amber-400/30 rounded-xl p-4 hover:border-amber-400/50 transition-all cursor-pointer relative"
            onClick={() => navigate('/pix?package=premium_monthly')}
            data-testid="expired-premium-plan"
          >
            <div className="absolute -top-2 right-3 bg-amber-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full">
              RECOMENDADO
            </div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                <span className="font-bold text-white">Premium</span>
              </div>
              <span className="text-amber-400 font-bold text-sm">R$ 29,90/mes</span>
            </div>
            <p className="text-xs text-gray-400">IA ilimitada + Organizador completo + Tudo</p>
          </div>
        </div>

        {/* CTA - Gerar Pix */}
        <Button
          className="w-full bg-gradient-to-r from-[#00FF94] to-emerald-500 text-black font-bold py-6 rounded-xl hover:opacity-90 transition-all"
          onClick={() => navigate('/pix?package=premium_monthly')}
          data-testid="expired-pix-cta"
        >
          <QrCode className="w-5 h-5 mr-2" />
          Gerar Pix e Renovar Agora
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>

        {/* Skip option */}
        <div className="text-center mt-4">
          <button 
            className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
            onClick={() => navigate('/pricing')}
            data-testid="expired-see-plans"
          >
            Ver todos os planos e pagar com cartao
          </button>
        </div>

        <div className="text-center mt-3">
          <button 
            className="text-xs text-gray-600 hover:text-gray-400 transition-colors"
            onClick={() => {
              sessionStorage.setItem('plan_expired_dismissed', 'true');
              setDismissed(true);
            }}
            data-testid="expired-go-home"
          >
            Continuar sem renovar
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PlanExpiredBlock;
